import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleCard } from '@/pages/home/components/ArticleList';
import { articles } from '@/mocks/articles';

const categoryNames: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = useMemo(() => {
    return articles.find((a) => a.id === articleId) || articles[0];
  }, [articleId]);

  const relatedArticles = useMemo(() => {
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article, article.category]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryNames[article.category] || article.category, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '番茄视频',
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <article className="pt-20">
        <div className="container-custom py-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <script type="application/ld+json">
          {JSON.stringify(articleLd)}
        </script>

        <header className="container-custom max-w-[800px] py-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium whitespace-nowrap">
              {categoryNames[article.category] || article.category}
            </span>
            <span className="text-sm text-foreground-400 whitespace-nowrap">{article.date}</span>
            <span className="text-sm text-foreground-400 whitespace-nowrap">{article.author}</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-base text-foreground-600 leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        <div className="container-custom max-w-[800px] pb-10">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:text-foreground-700 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="container-custom">
              <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </article>

      <Footer />
    </>
  );
}